import React from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import OutAndInpatientGraph from "../chart/OutAndInpatientGraph";
import GenderDistribution from "../chart/GenderDistribution";
import ReferalByPatient from "../chart/ReferalByPatient";
import PaymentType from "../chart/PaymentType";
import { ArrowRightAlt } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

const pieData = [
  { name: "Walk-In", value: 42 },
  { name: "Online", value: 58 },
];

const dummyPayment = {
  hmo: 60,
  outOfPocket: 40,
};

const prescriptionTrend = [
  // { name: "Sun", value: 0 },
  // { name: "Mon", value: 10 },
  // { name: "Tue", value: 12 },
  // { name: "Wed", value: 40 },
  // { name: "Thu", value: 35 },
  // { name: "Fri", value: 30 },
  // { name: "Sat", value: 38 },
];

const prescriptionCategories = [
  { name: "Cardiology", count: 0, color: "#00C49F" },
  { name: "General Medcine", count: 0, color: "#00C49F" },
  { name: "Antenatal", count: 0, color: "#00C49F" },
  // { name: "", count: 104, color: "#00C49F" },
  // { name: "Injury/Clinical", count: 104, color: "#00C49F" },
];

const paymentTypeData = {
  hmo: 60,
  outOfPocket: 40,
};

const dummyGender = {
  pharmAPatientPercentage: 60,
  pharmBPatientPercentage: 40,
  pharmCPatientPercentage: 20,
  pharmDPatientPercentage: 10,
  // ...gender
};

const COLORS = ["#00C49F", "#FFBB28"];

const StatCard = ({ label, number }) => (
  <Card
    sx={{
      minHeight: 170,
      minWidth: 280,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      "&:hover": {
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        transform: "translateY(-2px)",
        transition: "all 0.3s ease",
      },
    }}
  >
    <CardContent sx={{ textAlign: "center", p: 3 }}>
      <Typography
        variant="h4"
        sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}
      >
        {number}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontSize: "0.85rem" }}
      >
        {label}
      </Typography>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, children, dropdown = false }) => (
  <Card
    sx={{
      background: "#fff",
      padding: 2,
      borderRadius: 2,
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      height: "100%",
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "1rem" }}>
        {title}
      </Typography>
      {dropdown && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ cursor: "pointer" }}
        >
          Week ▼
        </Typography>
      )}
    </Box>
    {children}
  </Card>
);

export default function MainDashboard({
  statCards = [],
  assignedPatients,
  hmoPatients,
  outPatients,
  admitted,
  gender,
  onStatClick, // <-- add this prop
}) {
  const userInfo = JSON.parse(localStorage.getItem("USER_INFO"));
  const navigate = useNavigate();

  const [graphData, setgraphData] = React.useState([]);
  const [genderData, setgenderData] = React.useState([]);

  // If monthStats is provided, transform it to dummyGraphData format
  let dummyGraphData = [
    // { year: 2025, month: 1, outPatient: 30, inPatient: 20 },
    // { year: 2025, month: 2, outPatient: 50, inPatient: 35 },
    // { year: 2025, month: 3, outPatient: 40, inPatient: 25 },
    // { year: 2025, month: 4, outPatient: 60, inPatient: 45 },
    // { year: 2025, month: 5, outPatient: 70, inPatient: 50 },
    // { year: 2025, month: 6, outPatient: 55, inPatient: 38 },
  ];

  useEffect(() => {
    console.log("Month Stats: ", outPatients);
    if (outPatients && Array.isArray(outPatients.monthlyStats)) {
      dummyGraphData = outPatients.monthlyStats.map((stat) => {
        // console.log("Stat: ", stat.month);
        const dateObj = new Date(stat.month);
        // console.log("Stat: ", dateObj.getFullYear());

        return {
          year: dateObj.getFullYear(),
          month: dateObj.getMonth() + 1,
          day: dateObj.getDate(),
          outPatient: stat.outPatientCount,
          inPatient: stat.inPatientCount,
        };
      });
    }
    setgraphData(dummyGraphData);
    if (gender) {
      setgenderData(gender);
    }
  }, [outPatients]);

  console.log("Dummy Graph Data: ", dummyGraphData);

  // Default stat cards if none provided
  const defaultStatCards = [
    { label: "Awaiting Patients", number: assignedPatients },
    { label: "Administered Out-Patients", number: 4 },
    { label: "Admitted Patients", number: admitted },
    { label: "Total Consultations", number: 0 },
    { label: "Patients With HMOs", number: hmoPatients },
  ];
  const cardsToShow = statCards.length > 0 ? statCards : defaultStatCards;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "100vh" }}>
      {/* Header */}
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4 }}
      >
        <Grid item display="flex" alignItems="center">
          <Avatar
            sx={{
              width: 64,
              height: 64,
              mr: 3,
              backgroundColor: "primary.main",
            }}
            src={userInfo?.profilePicture || ""}
            alt={`${userInfo?.firstName} ${userInfo?.lastName}`}
          >
            <AccountCircleIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Good Day
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 0.5 }}>
              Dr. {userInfo?.firstName} {userInfo?.lastName}
            </Typography>
            {/* <Typography variant="body2" color="text.secondary">
              Healing Stripes Hospital, Lagos.
            </Typography> */}
          </Box>
        </Grid>
      </Grid>

      {/* Stat Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {cardsToShow.map((card, i) => (
          <Grid item xs={12} sm={6} md={2.4} key={i}>
            <div
              style={{ cursor: "pointer" }}
              onClick={() => onStatClick && onStatClick(card)}
            >
              <StatCard label={card.label} number={card.number} />
            </div>
          </Grid>
        ))}
      </Grid>

      <div className="graph-section">
        <div className="graph-container">
          <OutAndInpatientGraph propdata={graphData} />
        </div>
        <div className="gender-container">
          <ReferalByPatient propData={genderData} />
        </div>
      </div>

      {/* Bottom Three Chart Cards */}
      <div className="graph-section2">
        <ChartCard title="Consultation Trend" dropdown>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={prescriptionTrend}>
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#666" }}
              />
              <YAxis hide />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#00C49F"
                strokeWidth={3}
                dot={{ fill: "#00C49F", strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, fill: "#00C49F" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Prescription by Category */}

        <ChartCard title="Patients by Category" dropdown>
          <List sx={{ p: 0 }}>
            {prescriptionCategories.map((category, index) => (
              <ListItem key={index} sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <FiberManualRecordIcon
                    sx={{
                      fontSize: 12,
                      color: category.color,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={category.name}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {category.count}
                </Typography>
              </ListItem>
            ))}
          </List>
        </ChartCard>

        {/* <div className="gender-container">
          <PaymentType propData={paymentTypeData} />
        </div> */}
      </div>
    </Box>
  );
}
